import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData } from "../../../utility/Utility"; // only postData needed
import Swal from "sweetalert2";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";
import { format } from "date-fns";
import { quartersInYear } from "date-fns/constants";

function VoucherList() {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const rowsPerPage = 10;
  const statusMap = {
    pending: { label: "Pending", color: "text-warning" },
    issued: { label: "Approved", color: "text-success" },
    redeemed: { label: "Redeemed", color: "text-primary" },
    expired: { label: "Expired", color: "text-secondary" },
    revoked: { label: "Revoked", color: "text-danger" },
    cancelled: { label: "Cancelled", color: "text-danger" },
  };

  const fetchVouchers = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const response = await postData("api/xoxoday/vouchers/list", {
        search: search,
        page: page,
        limit: rowsPerPage,
        sort: { field: "createdAt", order: "desc" },
        filters: [
          { key: "status", value: "issued" },
          { key: "issuedAfter", value: "2025-01-01" },
        ],
      });

      if (response?.success) {
        setVouchers(response.data || []);
        setTotalPages(response.pagination?.totalPages || 0);
        setTotalItems(response.pagination?.totalItems || 0);
      } else {
        toast.error(response?.message || "Failed to fetch vouchers");
      }
    } catch (error) {
      toast.error("Error fetching vouchers");
      console.error("Fetch vouchers error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVouchers(currentPage + 1, searchTerm);
  }, [currentPage, searchTerm]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchVouchers(1, searchTerm);
  };

  const handleReset = () => {
    setSearchTerm("");
    setCurrentPage(0);
    fetchVouchers(1);
  };

  // NEW: Approve voucher
  // Approve voucher
  const handleApprove = async (voucher) => {
    const { value: expiryVoucherDate } = await Swal.fire({
      title: "Set Expiry Date",
      input: "date",
      inputAttributes: {
        min: new Date().toISOString().split("T")[0],
      },
      showCancelButton: true,
      confirmButtonText: "Approve",
      inputValidator: (value) => {
        if (!value) {
          return "⚠️ Please select a date before approving!";
        }
        return null;
      },
    });

    if (!expiryVoucherDate) return;

    try {
      const response = await postData("api/xoxoday/vouchers/approve", {
        voucherId: voucher?._id,
        expiryVoucherDate,
        quantity: 1,
        productId: voucher?.productId,
        denomination: voucher?.denomination,
      });

      if (response.success) {
        toast.success("Voucher approved successfully");
        fetchVouchers(currentPage + 1, searchTerm);
      } else {
        toast.error(response.message || "Failed to approve voucher");
      }
    } catch (error) {
      toast.error("Error approving voucher");
      console.error("Approve voucher error:", error);
    }
  };

  // NEW: Reject voucher
  // NEW: Reject voucher with reason
  const handleReject = async (voucherId) => {
    const { value: reason } = await Swal.fire({
      title: "Reject Voucher?",
      input: "text",
      inputLabel: "Reason for rejection",
      inputPlaceholder: "Enter reason...",
      showCancelButton: true,
      confirmButtonText: "Reject",
      cancelButtonText: "Cancel",
      inputValidator: (value) => {
        if (!value) {
          return "⚠️ Please enter a reason!";
        }
        return null;
      },
    });

    if (!reason) return;

    try {
      const response = await postData("api/xoxoday/vouchers/cancel", {
        voucherId,
        reason,
      });

      if (response?.success) {
        toast.success("Voucher rejected successfully");
        fetchVouchers(currentPage + 1, searchTerm);
      } else {
        toast.error(response?.message || "Failed to reject voucher");
      }
    } catch (error) {
      toast.error("Error rejecting voucher");
      console.error("Reject voucher error:", error);
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">Voucher Management</h4>
              <p className="fw-semibold my-2">Voucher List</p>
            </div>
          </div>

          {/* Search Form */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-center">
                      <div className="col-md-12 col-lg-4">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control ps-4"
                            placeholder="Search Vouchers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <span className="input-group-text">
                            <i className="mdi mdi-magnify"></i>
                          </span>
                        </div>
                      </div>

                      <div className="col-md-12 col-lg-3 d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          Search
                        </button>

                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Voucher Table */}
          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Voucher Name</th>
                            <th>Status</th>
                            <th>Issue Date</th>
                            <th>Amount</th>
                            <th>User Name</th>
                            <th>Mobile Number</th>
                            <th width="180px">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vouchers?.length > 0
                            ? vouchers.map((voucher, index) => (
                                <tr key={voucher._id}>
                                  <td>
                                    {currentPage * rowsPerPage + index + 1}
                                  </td>
                                  <td>{voucher?.productName}</td>
                                  <td
                                    className={
                                      statusMap[voucher.status]?.color ||
                                      "text-muted"
                                    }
                                  >
                                    {statusMap[voucher.status]?.label ||
                                      voucher.status}
                                  </td>
                                  <td>
                                    {voucher.issuedAt
                                      ? format(
                                          new Date(voucher.issuedAt),
                                          "dd MMM yyyy, hh:mm a"
                                        )
                                      : "N/A"}
                                  </td>
                                  <td>₹ {voucher?.denomination ?? "0"}</td>
                                  <td>{voucher?.userId?.fullName}</td>
                                  <td>{voucher?.userId?.mobile}</td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      <button
                                        className="btn btn-sm btn-success"
                                        onClick={() => handleApprove(voucher)}
                                        disabled={
                                          voucher.status === "issued" ||
                                          voucher.status === "redeemed" ||
                                          voucher.status === "cancelled"
                                        }
                                      >
                                        Approve
                                      </button>

                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() =>
                                          handleReject(voucher._id)
                                        }
                                        disabled={
                                          voucher.status === "issued" ||
                                          voucher.status === "redeemed" ||
                                          voucher.status === "cancelled"
                                        }
                                      >
                                        Reject
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            : !loading && (
                                <tr>
                                  <td colSpan="8" className="text-center py-4">
                                    No vouchers found
                                  </td>
                                </tr>
                              )}
                        </tbody>
                      </table>

                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={
                            <i className="mdi mdi-chevron-left"></i>
                          }
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VoucherList;
